import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import useBreakpoint from 'hooks/useBreakpoint';
import cx from 'classnames';
import dynamic from 'next/dynamic';

// Interfaces
import type { CompanyFiltersData } from 'components/Directory/CompanyFilters';
import type { ChipFilter } from 'components/Globals/FilterChips';

// Constants
import { STEP } from 'constants/constants';
import { UIFragments } from 'utils/log/constants/uiFragments';

// Utils
import { lettersSettings } from 'components/Globals/SlideFilter/responsiveSettings';
import type { CompaniesFilters, CompaniesItem, FilterItem, FilterRange } from 'api/fragments';
import nFormatter from 'utils/numberUtils';
import { trackEvent } from 'services/Gtm/functions';
import { UserPaywallContext } from 'providers/PaywallProvider';
import { generateAlphabet } from 'utils/stringUtils';
import RawHtml from 'utils/miscUtils/rawHtml';

// Components
import CompanyCard from 'components/Directory/CompanyCard';
import ObservableWrapper from 'components/ObservableWrapper/ObservableWrapper';
import InStream from 'components/Ad/AdSlot/InStream';
import ErrorBoundary from 'components/ErrorBoundary';

// Styles
import * as S from './CompanyDirectoryStyles';

const CompanySearch = dynamic(() => import('components/Directory/CompanySearch'));
const Button = dynamic(() => import('components/Globals/Base/Button'));
const AlphabetFilter = dynamic(() => import('components/Globals/SlideFilter'));
const CompanyFilters = dynamic(() => import('components/Directory/CompanyFilters'));

const ScrollUpButton = dynamic(() => import('components/Globals/Base/ScrollUpButton'), {
  ssr: false,
});
const SpinningLoader = dynamic(() => import('components/Loader/SpinningLoader'), {
  ssr: false,
});
const Placeholder = dynamic(() => import('components/Placeholder'), {
  ssr: false,
});
const CompanyCardSkeleton = dynamic(() => import('components/Directory/CompanyCard/CompanyCardSkeleton'), {
  ssr: false,
});
const FilterChips = dynamic(() => import('components/Globals/FilterChips'), {
  ssr: false,
});

interface CompaniesData {
  filterList: {
    countries: FilterItem[];
    industries: FilterItem[];
    rankings: FilterItem[];
    employeeRange: FilterRange;
    revenueRange: FilterRange;
  };
  items: CompaniesItem[];
  pagination: {
    currentPage: number;
    from: number | null;
    hasMorePages: boolean;
    lastPage: number;
    perPage: number;
    to: number | null;
    total: number;
  };
}

export interface CompanyDirectoryProps {
  companies: CompaniesData;
  mostPopular: { href: string; label: string }[];
  infiniteScroll: {
    taxonomy: string;
    slug: string;
    numCompaniesPerPage: number;
  };
  title: string;
  description: string;
  pageSize: number;
  pageNum: string;
}

const allLetters = ['All', '0-9', ...generateAlphabet()];

const CompanyDirectoryPage = ({
  companies,
  mostPopular,
  infiniteScroll,
  title,
  description,
  pageSize,
  pageNum,
}: CompanyDirectoryProps) => {
  const isTablet = useBreakpoint('lgMax');
  const [companyCards, setCompanyCards] = useState<CompaniesItem[]>([...companies.items]);
  const [shouldLoadMore, setShouldLoadMore] = useState(companies.pagination.total > infiniteScroll.numCompaniesPerPage);
  const [showLoader, setShowLoader] = useState(false);
  const [currentPageNum, setCurrentPageNum] = useState(parseInt(pageNum, 10));
  const [nextFocus, setNextFocus] = useState<number | undefined>();
  const [activeLetter, setActiveLetter] = useState('All');
  const [resultsNumber, setResultsNumber] = useState(companies.pagination.total);
  const {
    filterList: { employeeRange, revenueRange, countries },
  } = companies;
  const initialSelectedFilters: CompaniesFilters = useMemo(
    () => ({
      country: '',
      employees: {
        max: employeeRange.max,
        min: employeeRange.min,
      },
      firstLetter: '',
      industry: '',
      keyword: '',
      rankingId: -1,
      rankingYear: 0,
      revenues: {
        max: Math.round(revenueRange.max / STEP) * STEP,
        min: revenueRange.min,
      },
    }),
    [employeeRange.max, employeeRange.min, revenueRange.max, revenueRange.min],
  );
  const [selectedFilters, setSelectedFilters] = useState<CompaniesFilters>(initialSelectedFilters);
  const [searchTerm, setSearchTerm] = useState(selectedFilters.keyword);

  const filterData = useMemo(
    () =>
      ({
        ...companies.filterList,
        countries: countries.filter((country) => country.databaseId && country.title),
        resultsNumber: companies.pagination.total,
      }) as CompanyFiltersData,
    // Disabling this line because we know that companies prop we receive will not change
    // Companies is the data we already have when the page is pre-rendered by NextJS
    [],
  );

  const [chipFilters, setChipFilters] = useState<ChipFilter[]>([]);
  const [showChips, setShowChips] = useState(false);
  const skippedChips = ['firstLetter'];

  const filteredValuesQuery = (filters: CompaniesFilters) => {
    const skipRevenue =
      filters.revenues?.max === initialSelectedFilters.revenues.max &&
      filters.revenues?.min === initialSelectedFilters.revenues.min;
    const skipEmployee =
      filters.employees?.min === initialSelectedFilters.employees.min &&
      filters.employees?.max === initialSelectedFilters.employees.max;
    const filterSelectedValues = {
      country: filters.country,
      firstLetter: filters.firstLetter,
      industry: filters.industry,
      keyword: filters.keyword,
      ...(!skipEmployee && { maxEmployees: filters.employees?.max }),
      ...(!skipEmployee && { minEmployees: filters.employees?.min }),
      ...(!skipRevenue && { maxRevenue: filters.revenues?.max }),
      ...(!skipRevenue && { minRevenue: filters.revenues?.min }),
      rankingId: filters.rankingId,
      rankingYear: filters.rankingYear,
    };

    return Object.keys(filterSelectedValues)
      .map((key) => {
        const filterValue = filterSelectedValues[key as keyof typeof filterSelectedValues];

        return filterValue !== '' && filterValue !== -1 ? `${key}=${filterValue}` : '';
      })
      .filter((item) => item)
      .join('&');
  };

  const showMoreCompanies = useCallback(async () => {
    setShowLoader(true);
    const fetchUrl = `/api/page/${infiniteScroll.taxonomy}/${infiniteScroll.slug}/${
      currentPageNum + 1
    }/?${filteredValuesQuery(selectedFilters)}`;
    const response = await fetch(fetchUrl);
    const data = (await response.json()) as CompaniesData;
    setCompanyCards((prev) => [...prev, ...data.items]);
    setCurrentPageNum((prevPageNum) => prevPageNum + 1);
    setShowLoader(false);
    trackEvent({
      eventAction: `content load ${currentPageNum}`,
      eventCategory: 'navigation',
      eventLabel: 'scroll',
    });
  }, [currentPageNum, infiniteScroll.slug, infiniteScroll.taxonomy, selectedFilters, filteredValuesQuery]);

  const getCompaniesBasedOnFilters = async (filters: CompaniesFilters) => {
    setShowLoader(true);
    setCurrentPageNum(1);
    const fetchUrl = `/api/page/${infiniteScroll.taxonomy}/${infiniteScroll.slug}/1/?${filteredValuesQuery(filters)}`;
    const response = await fetch(fetchUrl);
    const data = (await response.json()) as CompaniesData;
    setCompanyCards(() => [...data.items]);
    setResultsNumber(data.pagination.total);
    setShowLoader(false);
  };

  const handleUpdateLetter = (newLetter: string) => {
    const chosenLetter = newLetter === '0-9' ? 'digit' : newLetter;

    setActiveLetter(newLetter);
    setCompanyCards([]);
    setCurrentPageNum(1);
    setSelectedFilters((prev) => ({ ...prev, firstLetter: chosenLetter === 'All' ? '' : chosenLetter, keyword: '' }));
    void getCompaniesBasedOnFilters({
      ...selectedFilters,
      firstLetter: chosenLetter === 'All' ? '' : chosenLetter,
      keyword: '',
    } as CompaniesFilters);
  };

  const buildChips = (filters: CompaniesFilters) =>
    Object.entries(filters).map(
      ([key, value]: [key: string, value: { max: number; min: number } | string | number]) => {
        const chipSuffix = { employees: ' (Employees) ', revenues: ' ($M) ' };
        const filterValue = filters[key as keyof typeof filters];
        if (skippedChips.includes(key)) return {} as ChipFilter;

        if (typeof filterValue === 'object') {
          const initialKeyValue = initialSelectedFilters[key as keyof typeof initialSelectedFilters];
          const selectedKeyValue = filters[key as keyof typeof filters];
          if (JSON.stringify(initialKeyValue) === JSON.stringify(selectedKeyValue)) return {} as ChipFilter;

          const label = Object.values(filterValue)
            .sort((firstVal, secondVal) => firstVal - secondVal)
            .map((val) => nFormatter(val))
            .join(' - ');
          return {
            keyName: key,
            value: label + chipSuffix[key as keyof typeof chipSuffix],
          };
        }
        if (key === 'rankingId') {
          return {
            keyName: key,
            value: filterData.rankings.find((rank) => rank.databaseId === value.toString())?.title,
          };
        }
        return {
          keyName: key,
          value,
        };
      },
    ) as ChipFilter[];

  const handleSearch = (keyword: string) => {
    setCompanyCards([]);
    setCurrentPageNum(1);
    setChipFilters([]);
    setActiveLetter('All');
    setSearchTerm(keyword);

    const searchFilters = { ...initialSelectedFilters, keyword };
    setSelectedFilters(searchFilters);
    void getCompaniesBasedOnFilters(searchFilters);

    const chips = buildChips(searchFilters);
    setChipFilters([...chips]);
    setShowChips(true);
  };

  const handleRemoveFilterChip = (key: string) => {
    const updatedFilters = { ...selectedFilters, [key]: initialSelectedFilters[key as keyof CompaniesFilters] };

    if (key === 'rankingId') {
      updatedFilters.rankingId = initialSelectedFilters.rankingId;
      updatedFilters.rankingYear = initialSelectedFilters.rankingYear;
    }

    setSelectedFilters(updatedFilters);
    setSearchTerm(updatedFilters.keyword);
    void getCompaniesBasedOnFilters(updatedFilters);
    const chips = buildChips(updatedFilters);
    setChipFilters(chips);
    setShowChips(true);
  };

  const handleApplyFilters = useCallback((filters: CompaniesFilters) => {
    setSelectedFilters((prev) => ({ ...prev, ...filters }));
    setCompanyCards([]);
    void getCompaniesBasedOnFilters(filters);
    setActiveLetter('All');

    const chips = buildChips(filters);
    setChipFilters([...chips]);
    setShowChips(true);
  }, []);

  const handleResetFilters = useCallback(() => {
    setChipFilters([]);
    setSelectedFilters(initialSelectedFilters);
    setCompanyCards([...companies.items]);
    setResultsNumber(companies.pagination.total);
    setActiveLetter('All');
  }, [companies.items, companies.pagination.total, initialSelectedFilters]);

  useEffect(() => {
    setShouldLoadMore(resultsNumber > currentPageNum * infiniteScroll.numCompaniesPerPage);
  }, [currentPageNum, resultsNumber, infiniteScroll.numCompaniesPerPage]);

  const { paywallCheck } = useContext(UserPaywallContext).paywallState;

  const renderAds = paywallCheck === 'no-paywall';

  return (
    <S.Wrapper>
      {/** Hero Section */}
      <ErrorBoundary fragment={UIFragments.COMPANIES_PAGE}>
        <S.Hero>
          <S.HeroTitle
            as='h2'
            $fontFamily='graphikCond'
            $size={{ default: 'level2', lgMin: 'level1' }}
            data-cy='title'
          >
            {title}
          </S.HeroTitle>
          <S.HeroDescription data-cy='description'>{RawHtml({ html: description })}</S.HeroDescription>
        </S.Hero>
      </ErrorBoundary>

      <S.ContentWrapper>
        {/** Mobile search Section */}
        <ErrorBoundary fragment={UIFragments.COMPANIES_PAGE}>
          {isTablet && (
            <CompanySearch
              searchTerm={searchTerm}
              mostPopular={mostPopular}
              placeholder='Search Fortune...'
              onSearch={handleSearch}
              onClear={() => handleSearch('')}
              dataCy='cd-search'
            />
          )}
        </ErrorBoundary>

        <ErrorBoundary fragment={UIFragments.COMPANIES_PAGE}>
          <S.SidebarWrapper key='sticky-sidebar'>
            <S.Sidebar>
              <CompanyFilters
                defaultData={{ ...selectedFilters, firstLetter: '' }}
                filterData={filterData}
                onApplyFiltersClick={handleApplyFilters}
                onResetFiltersClick={handleResetFilters}
              />
            </S.Sidebar>
          </S.SidebarWrapper>
        </ErrorBoundary>

        <S.MainContent>
          {/** Mobile Filter section */}
          <ErrorBoundary fragment={UIFragments.COMPANIES_PAGE}>
            {isTablet && (
              <CompanyFilters
                defaultData={{ ...selectedFilters, firstLetter: '' }}
                filterData={filterData}
                onApplyFiltersClick={handleApplyFilters}
                onResetFiltersClick={handleResetFilters}
              />
            )}
          </ErrorBoundary>
          {/** Search Section */}
          <ErrorBoundary fragment={UIFragments.COMPANIES_PAGE}>
            {!isTablet && (
              <CompanySearch
                searchTerm={searchTerm}
                mostPopular={mostPopular}
                placeholder='Search Fortune...'
                onSearch={handleSearch}
                onClear={() => handleSearch('')}
                dataCy='cd-search'
              />
            )}
          </ErrorBoundary>

          {/** Letter picker */}
          <ErrorBoundary fragment={UIFragments.COMPANIES_PAGE}>
            <S.LetterPickerWrapper className={cx({ loading: showLoader })}>
              <AlphabetFilter
                className='letter-picker'
                dataCy='alphabet-filter'
                valueList={allLetters}
                responsiveSettings={lettersSettings}
                selectedValue={activeLetter}
                changeSelectedValue={(newLetter) => handleUpdateLetter(newLetter)}
              />
            </S.LetterPickerWrapper>
          </ErrorBoundary>

          <ErrorBoundary fragment={UIFragments.COMPANIES_PAGE}>
            <FilterChips
              showChips={showChips}
              chipFilters={chipFilters}
              handleRemoveFilterChip={handleRemoveFilterChip}
              data-cy='chips'
            />
          </ErrorBoundary>

          {showLoader ? (
            <Placeholder
              className='placeholder'
              size={{ height: 24, width: 150 }}
            />
          ) : (
            <S.ResultText data-cy='results'>{resultsNumber} companies/results</S.ResultText>
          )}
          {companyCards.length <= 0 && !showLoader && (
            <S.ResultText
              className='bold'
              data-cy='no-results'
            >
              No results found matching your search criteria. Please search again.
            </S.ResultText>
          )}
          {showLoader && (
            <S.CompaniesWrapper>
              {new Array(9).fill(1).map((_, index) => (
                <CompanyCardSkeleton key={index} />
              ))}
            </S.CompaniesWrapper>
          )}
          <ErrorBoundary fragment={UIFragments.COMPANIES_PAGE}>
            {companyCards.length > 0 && (
              <S.CompaniesWrapper data-cy='cards-wrapper'>
                {companyCards.map((company, index) => (
                  <ObservableWrapper
                    key={company.databaseId}
                    isVisible={() => void showMoreCompanies()}
                    isLast={
                      index + 1 === companyCards.length &&
                      currentPageNum % 3 !== 0 &&
                      resultsNumber > companyCards.length
                    }
                    focus={index === nextFocus}
                  >
                    <CompanyCard
                      className={cx({ loading: showLoader && currentPageNum === 1 && shouldLoadMore })}
                      key={company.name}
                      title={company.name}
                      hasBadge={company.showBadge}
                      country={company.country}
                      revenues={company.revenues}
                      pageLink={company.permalink}
                      dataCy='card'
                    />
                  </ObservableWrapper>
                ))}
              </S.CompaniesWrapper>
            )}
          </ErrorBoundary>

          {companyCards.length >= pageSize && companyCards.length < companies.pagination.total && (
            <S.SpinnerWrapper>
              <Button
                className={`show-more ${shouldLoadMore && currentPageNum % 3 === 0 ? '' : 'hidden'}`}
                onClick={() => {
                  setNextFocus(currentPageNum * infiniteScroll.numCompaniesPerPage);
                  void showMoreCompanies();
                }}
                variant='secondary'
                size={{ default: 'small' }}
                hasRightArrow={false}
              >
                Load More
              </Button>
              <SpinningLoader
                className='spinning-loader'
                show={showLoader}
              />
            </S.SpinnerWrapper>
          )}
          <ScrollUpButton dataCy='cd-scroll-top-button' />
        </S.MainContent>
      </S.ContentWrapper>
      <div>
        <InStream
          index={0}
          renderAds={renderAds}
        />
      </div>
    </S.Wrapper>
  );
};

export default CompanyDirectoryPage;
